// New dependency: @tanstack/react-query
// Reason: schedule polling and invalidate subscription queries without manual setInterval wiring.
import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { billingApi, pendingConfirmationStore } from './billingApi';
import type { Subscription } from './types';
import './BillingConfirmationProvider.css';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { billingQueryKeys } from './billing.queries';
import { WEBHOOK_POLL_INTERVAL_MS, WEBHOOK_POLL_TIMEOUT_MS } from './billing.constants';
import { billingStrings } from './billing.strings';

type ConfirmationPhase = 'idle' | 'awaiting' | 'success' | 'timeout' | 'failed';

type BillingConfirmationContextValue = {
  phase: ConfirmationPhase;
  subscription: Subscription | null;
  message: string;
  retry: () => void;
};

export const BillingConfirmationContext = createContext<BillingConfirmationContextValue>({
  phase: 'idle',
  subscription: null,
  message: '',
  retry: () => {}
});

const buildMessage = (phase: ConfirmationPhase): string => {
  switch (phase) {
    case 'awaiting':
      return billingStrings.webhookAwaitingBanner;
    case 'success':
      return billingStrings.webhookSuccessBanner;
    case 'timeout':
      return billingStrings.webhookTimeoutBanner;
    case 'failed':
      return billingStrings.webhookFailedBanner;
    default:
      return '';
  }
};

const Banner = ({
  phase,
  message,
  onRetry
}: {
  phase: ConfirmationPhase;
  message: string;
  onRetry: () => void;
}) => {
  if (phase === 'idle') {
    return null;
  }
  const tone = phase === 'success' ? 'success' : phase === 'timeout' ? 'warning' : phase === 'failed' ? 'danger' : 'info';

  return (
    <div
      aria-live="polite"
      role="status"
      className={`billing-confirm-banner billing-confirm-banner-${tone}`}
    >
      <span style={{ fontSize: 14, lineHeight: 1.35 }}>{message}</span>
      {(phase === 'timeout' || phase === 'failed') && (
        <button
          type="button"
          onClick={onRetry}
          className="btn ghost"
        >
          Retry
        </button>
      )}
    </div>
  );
};

const BillingConfirmationProvider = ({ children }: { children: React.ReactNode }) => {
  const [phase, setPhase] = useState<ConfirmationPhase>('idle');
  const [message, setMessage] = useState<string>('');
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [retryTick, setRetryTick] = useState(0);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const queryClient = useQueryClient();

  // Store the success-banner auto-dismiss timeout so we can cancel it on unmount.
  // Without this, setPhase('idle') fires on an unmounted component after 6 seconds.
  const dismissTimerRef = useRef<number | null>(null);

  const retry = () => setRetryTick((n) => n + 1);

  const [pending, setPending] = useState(() => pendingConfirmationStore.get());
  useEffect(() => {
    setPending(pendingConfirmationStore.get());
  }, [retryTick]);

  // Clean up the dismiss timer on unmount to prevent state updates on dead components.
  useEffect(() => {
    return () => {
      if (dismissTimerRef.current !== null) {
        window.clearTimeout(dismissTimerRef.current);
      }
    };
  }, []);

  const enabled = Boolean(pending?.eventId);

  const isTimedOut = startedAt !== null && Date.now() - startedAt > WEBHOOK_POLL_TIMEOUT_MS;

  // If the pending entry's timestamp is already past the poll window on first render,
  // run one immediate fetch (enabled=true, refetchInterval=false) instead of going
  // straight to timeout. The subscription may already be ACTIVE from a previous webhook.
  const shouldPoll = enabled && !isTimedOut;
  const shouldFetchOnce = enabled && isTimedOut;

  const confirmationQuery = useQuery({
    queryKey: ['billing', 'confirmation', pending?.eventId, retryTick],
    queryFn: async () => billingApi.refreshSubscriptionStatus(),
    enabled: shouldPoll || shouldFetchOnce,
    refetchInterval: shouldPoll ? WEBHOOK_POLL_INTERVAL_MS : false,
    staleTime: 0,
    retry: 0
  });

  useEffect(() => {
    if (!pending) {
      setPhase('idle');
      setMessage('');
      setStartedAt(null);
      return () => {};
    }

    // Only show "awaiting" if we haven't already passed the timeout window.
    // If the pending entry is stale (e.g. user returned to the page after a long
    // break within the same tab), skip straight to a single status fetch without
    // showing the full awaiting countdown.
    if (!isTimedOut) {
      setPhase('awaiting');
      setMessage(buildMessage('awaiting'));
    }
    if (!startedAt) {
      setStartedAt(pending.timestamp || Date.now());
    }
    return () => {};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  useEffect(() => {
    if (!pending || !isTimedOut) {
      return;
    }
    // Only set timeout phase if the single fetch hasn't resolved yet.
    if (!confirmationQuery.data) {
      setPhase('timeout');
      setMessage(buildMessage('timeout'));
    }
  }, [isTimedOut, pending, confirmationQuery.data]);

  useEffect(() => {
    if (!pending) {
      return;
    }
    if (confirmationQuery.isError) {
      setPhase('failed');
      setMessage(buildMessage('failed'));
      return;
    }
    if (!confirmationQuery.data) {
      return;
    }
    const next = confirmationQuery.data as Subscription;
    setSubscription(next);

    // Invalidate subscription query after each polling tick.
    queryClient.invalidateQueries({ queryKey: billingQueryKeys.subscription() });

    if (next.status === 'ACTIVE') {
      setPhase('success');
      setMessage(buildMessage('success'));
      pendingConfirmationStore.clear();
      setStartedAt(null);
      // Auto-dismiss the success banner after 6 seconds. Store the timer ID so
      // it can be cancelled if the component unmounts before it fires.
      if (dismissTimerRef.current !== null) {
        window.clearTimeout(dismissTimerRef.current);
      }
      dismissTimerRef.current = window.setTimeout(() => {
        dismissTimerRef.current = null;
        setPhase('idle');
        setMessage('');
      }, 6_000);
    }
  }, [confirmationQuery.data, confirmationQuery.isError, pending, queryClient]);

  const value = useMemo(() => ({
    phase,
    subscription,
    message,
    retry
  }), [phase, subscription, message]);

  return (
    <BillingConfirmationContext.Provider value={value}>
      {children}
      <Banner phase={phase} message={message} onRetry={retry} />
    </BillingConfirmationContext.Provider>
  );
};

export default BillingConfirmationProvider;
